import { useOverride } from '@/lib/debug'
import { logger } from '@/ui'
import {
	authLogin,
	authLogout,
	authRefresh,
	authRegister,
	authSwitchOrganisation,
	authVerifyRegistration,
	permissionIndex,
	RoleEnumKey,
} from '@jobilla/nexus-sdk'
import type {
	AuthLoginError,
	AuthRegisterError,
	AuthSwitchOrganisationError,
	AuthVerifyRegistrationError,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AuthContext, type AuthUser } from './auth-context'

// Module-scoped token — never in localStorage
let accessToken: string | null = null

const withCredentials = { withCredentials: true } as const

function authHeaders(): Record<string, string> {
	return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
}

export type AuthErrorData =
	| AuthLoginError
	| AuthRegisterError
	| AuthVerifyRegistrationError
	| AuthSwitchOrganisationError

export class AuthError extends Error {
	constructor(
		public readonly status: number,
		message: string,
		public readonly data?: AuthErrorData,
	) {
		super(message)
		this.name = 'AuthError'
	}
}

interface ApiErrorEntry {
	code?: string
	message?: string
	suggestions?: string[]
}

interface ApiErrorData {
	code?: string
	message?: string
	errors: ApiErrorEntry[]
}

function extractErrorData(err: unknown): ApiErrorData | null {
	const data = resolveErrorData(err)
	if (!data) {
		return null
	}
	const errors: ApiErrorEntry[] = []
	if (Array.isArray(data.errors)) {
		// WorkOS-style: [{ code, message, suggestions? }, ...]
		for (const entry of data.errors) {
			const e = entry as Record<string, unknown>
			const suggestions = Array.isArray(e.suggestions)
				? (e.suggestions as unknown[]).filter((s): s is string => typeof s === 'string')
				: undefined
			errors.push({
				code: typeof e.code === 'string' ? e.code : undefined,
				message: typeof e.message === 'string' ? e.message : undefined,
				suggestions: suggestions && suggestions.length > 0 ? suggestions : undefined,
			})
		}
	} else if (data.errors && typeof data.errors === 'object') {
		// Laravel validation-style: { field: ["msg1", "msg2"], ... }
		for (const messages of Object.values(data.errors as Record<string, unknown>)) {
			if (Array.isArray(messages)) {
				for (const msg of messages) {
					if (typeof msg === 'string') {
						errors.push({ message: msg })
					}
				}
			}
		}
	}
	return {
		code: typeof data.code === 'string' ? data.code : undefined,
		message: typeof data.message === 'string' ? data.message : undefined,
		errors,
	}
}

export function extractErrorMessage(err: unknown, fallback = 'Authentication failed'): string {
	const data = extractErrorData(err)
	if (!data) {
		return fallback
	}
	if (data.errors.length > 0) {
		const messages = data.errors
			.map((entry) => entry.message)
			.filter((msg): msg is string => typeof msg === 'string')
		if (messages.length > 0) {
			return messages.join(' ')
		}
	}
	if (data.message) {
		return data.message
	}
	return fallback
}

function resolveErrorData(err: unknown): Record<string, unknown> | null {
	if (err && typeof err === 'object') {
		// Axios-style: err.response.data
		const responseData = (err as { response?: { data?: unknown } }).response?.data
		if (responseData && typeof responseData === 'object') {
			return responseData as Record<string, unknown>
		}
		// Plain object with message/errors keys (already resolved data)
		if ('message' in err || 'errors' in err) {
			return err as Record<string, unknown>
		}
	}
	// JSON string
	if (typeof err === 'string' && err.startsWith('{')) {
		try {
			return JSON.parse(err) as Record<string, unknown>
		} catch {
			return null
		}
	}
	return null
}

function isAuthErrorData(data: unknown): data is AuthErrorData {
	if (!data || typeof data !== 'object') {
		return false
	}
	return 'code' in data || 'message' in data || 'errors' in data || 'error' in data
}

function toAuthError(error: unknown): never {
	if (!(error instanceof AxiosError)) {
		throw error
	}

	const body: unknown = error.response?.data
	const status = error.response?.status ?? 0

	if (!body) {
		throw new AuthError(status, 'Authentication failed')
	}

	// AxiosError types `response.data` as `any`, so the guard is the only thing
	// keeping an arbitrary body out of the typed `data` field.
	throw new AuthError(
		status,
		extractErrorMessage(body),
		isAuthErrorData(body) ? body : undefined,
	)
}

interface ApiAuthUser {
	id: string
	email: string
	firstName: string | null
	lastName: string | null
	profilePictureUrl: string | null
	language: AuthUser['language']
	firstLoginAt: string | null
	onboardingCompletedAt: string | null
	recruitingVolume: AuthUser['recruitingVolume']
	contactsCandidates: boolean | null
	managesTeam: boolean | null
	organisationHeadcount: AuthUser['organisationHeadcount']
	phoneNumber: string | null
	isSelfSignup: boolean
	isProvisioned: boolean
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
	try {
		const base64Url = token.split('.')[1]
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		return JSON.parse(atob(base64))
	} catch {
		return null
	}
}

export function NexusAuthProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient()
	const [user, setUser] = useState<AuthUser | null>(null)
	const [role, setRole] = useState<RoleEnumKey | null>(null)
	const [permissions, setPermissions] = useState<string[]>([])
	const roleOverride = useOverride('role') ?? null
	const effectiveRole = roleOverride ?? role
	const [isLoading, setIsLoading] = useState(true)
	const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const refreshPromiseRef = useRef<Promise<void> | null>(null)
	const refreshSessionRef = useRef<(() => Promise<void>) | undefined>(undefined)
	const currentOrgRef = useRef<string | null>(null)

	const scheduleRefresh = useCallback((token: string) => {
		if (refreshTimerRef.current) {
			clearTimeout(refreshTimerRef.current)
		}
		const exp = decodeJwtPayload(token)?.exp as number | undefined
		if (!exp) { return }

		// Refresh 30 seconds before expiry
		const msUntilRefresh = (exp * 1000) - Date.now() - 30_000
		if (msUntilRefresh <= 0) {
			void refreshSessionRef.current?.()
			return
		}

		refreshTimerRef.current = setTimeout(() => {
			void refreshSessionRef.current?.()
		}, msUntilRefresh)
	}, [])

	const setAuthState = useCallback((apiUser: ApiAuthUser, token: string) => {
		accessToken = token
		currentOrgRef.current = (decodeJwtPayload(token)?.org_id as string | null) ?? null
		setUser(apiUser)
		scheduleRefresh(token)
	}, [scheduleRefresh])

	const clearAuthState = useCallback(() => {
		accessToken = null
		currentOrgRef.current = null
		setUser(null)
		setRole(null)
		setPermissions([])
		if (refreshTimerRef.current) {
			clearTimeout(refreshTimerRef.current)
			refreshTimerRef.current = null
		}
	}, [])

	const fetchPermissions = useCallback(async () => {
		if (!accessToken) { return }
		try {
			const { data } = await permissionIndex({ headers: authHeaders(), ...withCredentials })
			setRole(data.role)
			setPermissions(data.permissions)
		} catch {
			// If permissions fetch fails, keep current state
		}
	}, [])

	const isInitialOverrideRef = useRef(true)
	useEffect(() => {
		if (isInitialOverrideRef.current) {
			isInitialOverrideRef.current = false
			return
		}
		void fetchPermissions()
	}, [roleOverride, fetchPermissions])

	const refreshSession = useCallback(async () => {
		// Deduplicate concurrent refresh calls — if one is already in-flight, piggyback on it
		if (refreshPromiseRef.current) {
			return refreshPromiseRef.current
		}

		const promise = (async () => {
			try {
				const { data } = await authRefresh(
					currentOrgRef.current ? { organisationId: currentOrgRef.current } : {},
					{ headers: authHeaders(), ...withCredentials },
				)
				setAuthState(data.user, data.accessToken)
				await fetchPermissions()
			} catch (error) {
				logger.warn('Token refresh failed, clearing session', {
					category: 'auth',
					data: { error: error instanceof Error ? error.message : String(error) },
				})
				clearAuthState()
			}
		})()

		refreshPromiseRef.current = promise
		promise.finally(() => {
			refreshPromiseRef.current = null
		})

		return promise
	}, [setAuthState, clearAuthState, fetchPermissions])

	useEffect(() => {
		refreshSessionRef.current = refreshSession
	}, [refreshSession])

	// Restore session on mount, scoped to the org in the URL if present.
	// Skip for public routes — they don't need a session and shouldn't trigger auth calls.
	const isPublicRoute = window.location.pathname.startsWith('/book/')
	useEffect(() => {
		if (isPublicRoute) {
			setIsLoading(false)
			return
		}

		let cancelled = false

		async function restore() {
			try {
				const { data } = await authRefresh(
					{},
					{ headers: authHeaders(), ...withCredentials },
				)
				if (cancelled) { return }
				setAuthState(data.user, data.accessToken)
				await fetchPermissions()
			} catch {
				// No valid session — that's fine
			} finally {
				if (!cancelled) {
					setIsLoading(false)
				}
			}
		}

		void restore()

		return () => {
			cancelled = true
			if (refreshTimerRef.current) {
				clearTimeout(refreshTimerRef.current)
			}
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const signIn = useCallback(async (email: string, password: string) => {
		try {
			const { data } = await authLogin({ email, password }, { headers: authHeaders(), ...withCredentials })
			setAuthState(data.user, data.accessToken)
			await fetchPermissions()
		} catch (error) {
			toAuthError(error)
		}
	}, [setAuthState, fetchPermissions])

	const signUp = useCallback(async (email: string, password: string, firstName: string, lastName: string) => {
		try {
			await authRegister({
				email,
				password,
				firstName,
				lastName,
			}, { headers: authHeaders(), ...withCredentials })
		} catch (error) {
			toAuthError(error)
		}
	}, [])

	const verifyRegistration = useCallback(async (email: string, code: string, password: string) => {
		try {
			const { data } = await authVerifyRegistration(
				{ email, code, password },
				{ headers: authHeaders(), ...withCredentials },
			)
			setAuthState(data.user, data.accessToken)
			await fetchPermissions()
		} catch (error) {
			toAuthError(error)
		}
	}, [setAuthState, fetchPermissions])

	const signOut = useCallback(async () => {
		try {
			await authLogout({ headers: authHeaders(), ...withCredentials })
		} catch {
			// Best effort
		}
		clearAuthState()
	}, [clearAuthState])

	const getAccessToken = useCallback(async () => {
		return accessToken
	}, [])

	// @TODO: scope query keys by org ID instead of clearing the entire cache on switch
	const switchOrganisation = useCallback(async (workosOrgId: string) => {
		if (currentOrgRef.current === workosOrgId) {
			return
		}
		try {
			const { data } = await authSwitchOrganisation({
				organisationId: workosOrgId,
			})
			setAuthState(data.user, data.accessToken)
			void queryClient.invalidateQueries()
			await fetchPermissions()
		} catch (error) {
			toAuthError(error)
		}
	}, [setAuthState, fetchPermissions, queryClient])

	const can = useCallback((permission: string) => {
		return permissions.includes(permission)
	}, [permissions])

	const hasRole = useCallback((r: RoleEnumKey) => {
		return effectiveRole === r
	}, [effectiveRole])

	const value = useMemo(() => ({
		user,
		role: effectiveRole,
		permissions,
		isLoading,
		isSignedIn: !!user,
		signIn,
		signUp,
		verifyRegistration,
		signOut,
		getAccessToken,
		can,
		hasRole,
		refreshSession,
		fetchPermissions,
		switchOrganisation,
	}), [
		user,
		effectiveRole,
		permissions,
		isLoading,
		signIn,
		signUp,
		verifyRegistration,
		signOut,
		getAccessToken,
		can,
		hasRole,
		refreshSession,
		fetchPermissions,
		switchOrganisation,
	])

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	)
}
